import { type Locale } from 'invictus-sdk-typescript'

import { allLocales, DEFAULT_LOCALE } from '@Constants/locale'
import { HOME_PATH } from '@Routes/routes'
import { type Pathname } from '@Types/navigation/navigation'

const isDevelopment = process.env.NODE_ENV !== 'production'
// TODO: SIBR-4987: Séparer les fonctions appelées dans Next et celle du Front
// process.env ne renvoie rien dans le context client CSR
const basePath = process.env.BASE_PATH || '/app'

export const isLocalhostUrl = (url: string): boolean => {
  try {
    const { hostname } = new URL(url)

    return hostname === 'localhost' || hostname === '127.0.0.1'
  } catch {
    return false
  }
}

export const isExternalUrl = (url: string): boolean => url.includes('://')

// TODO: A gérer, internationalisation de l'url
const DRUPAL_NOT_FOUND_URL = '/document-non-trouve'
const DRUPAL_ERROR_URL = '/erreur-interne'

export const isPathname = (href: string | undefined): href is Pathname => !!href?.startsWith('/')

export const getPathname = (asPath: string): string => asPath.split('?')[0]

/**
 * NextJS automatically removes the locale prefix in the current URL when the locale is same as the default locale "fr"
 * You probably want to skip `basePath` if used in redirect with next router as it will be added automatically in URL
 * @param pathname
 * @param locale
 * @param skipBasePath
 */
export const getRelativeUrl = (pathname: Pathname, locale: Locale = DEFAULT_LOCALE, skipBasePath = false): string =>
  `${skipBasePath ? '' : basePath}${locale === DEFAULT_LOCALE ? '' : `/${locale}`}${pathname}`

export const getAbsoluteUrl = (pathname: Pathname, locale: Locale = DEFAULT_LOCALE, skipBasePath = false): string =>
  `${window.location.origin}${getRelativeUrl(pathname, locale, skipBasePath)}`

export const getFrontendPublicUrl = (
  frontendPublicUrl: string,
  pathname: Pathname,
  locale: Locale = DEFAULT_LOCALE,
  skipBasePath = false,
): string => `${frontendPublicUrl}${getRelativeUrl(pathname, locale, skipBasePath)}`

export const hasBasePathPrefix = (url: string): boolean => !!basePath && url.startsWith(basePath)

export const addBasePathPrefix = (pathname: Pathname): string => `${basePath}${pathname}`

export const removeBasePathPrefix = (url: string): string => (hasBasePathPrefix(url) ? url.slice(basePath.length) : url)

export const redirectTo404 = (): void =>
  window.location.replace(isDevelopment || isLocalhostUrl(window.location.href) ? basePath : DRUPAL_NOT_FOUND_URL)

export const redirectToError = (): void => window.location.replace(DRUPAL_ERROR_URL)

export const isHomepage = (path: string | undefined): boolean => !!path && path === HOME_PATH

const localeOrParamRegex = new RegExp(String.raw`^/(${allLocales.join('|')}|\[locale\])(/|\?|$)(.*)`, 'i')

const getLocalePrefixMatch = (path: string): RegExpExecArray | null =>
  localeOrParamRegex.exec(removeBasePathPrefix(path))

export const getPathLocale = (path: string): Locale | undefined => {
  const locale = getLocalePrefixMatch(path)?.[1]

  if (!locale || locale === '[locale]') {
    return undefined
  }

  return locale as Locale
}

export const getPreviousLocaleFromReferer = (
  referer: string | undefined,
  frontendPublicUrl: string | undefined,
): Locale | undefined => {
  if (!referer || !frontendPublicUrl) {
    return undefined
  }

  try {
    const { host: refererHost, pathname: refererPathname } = new URL(referer)
    const { host: publicHost } = new URL(frontendPublicUrl)

    if (refererHost !== publicHost) {
      return undefined
    }

    return getPathLocale(refererPathname) ?? DEFAULT_LOCALE
  } catch {
    return undefined
  }
}

export const getPathWithoutLocale = (path: string): string => {
  const pathWithoutBase = removeBasePathPrefix(path)
  const match = getLocalePrefixMatch(path)

  if (!match) {
    return pathWithoutBase
  }

  const [, , separator, rest] = match

  return `${separator}${rest}` || '/'
}
